import React, { useEffect, useContext, useState } from 'react'
import * as yup from 'yup'
import { GridItem } from '@zuto/core/src/grid'
import { useCallbackOnce } from '@zuto/utilities'
import QuestionKey from '../../../../Questions/question-key'
import AppFormQuestion from '../../../AppFormQuestion'
import { ApplicationFormContext } from '../../../ApplicationForm/ApplicationFormContext'
import withFade from '../../../HOC/withFade'
import NavigationButtons from '../../../NavigationButtons/NavigationButtons'
import LengthOfTime from '../../../LengthOfTime'
import LengthOfTimeEntry, { buildLengthOfTimeEntry } from '../../../LengthOfTime/LengthOfTimeEntry'
import AddressHistoryEntryContext from '../AddressEntry/AddressHistoryEntryContext'
import { AddressEntryErrors } from '../AddressHistoryContext'
import AddressHistoryValidator from '../../../../validators/AddressHistoryValidator'
import { questionFontStyle, fireReactGAEvent } from '../../utils'
import { HistoryIndex } from '../../HistoryEntry/HistoryEntry'
import { calculateTotalMonths } from '../../../../utils/utils'
import { PrefillReviewPageApplicationFormContext, YearsMonthsDuration } from '../../../ApplicationForm/PrefillReviewPageApplicationFormContext'
import { UpdateFollowingPrefillStepType } from '../../../../Questions/question-component-props'
import { useAppFormEventsService } from '../../../../hooks/useAppFormEventsService'

interface TimeInAddressSituationProps {
  existingDuration?: LengthOfTimeEntry
  entryId: HistoryIndex
  disablePrevious?: boolean
  entryTitle?: string
  updateFollowingPrefillStep?: UpdateFollowingPrefillStepType
}
interface AddressDuration {
  [entryId: string]: YearsMonthsDuration
}

type Errors = {
  message?: string
  fields: string[]
}

const TimeInAddressSituation = ({ existingDuration, entryId, disablePrevious = false, entryTitle, updateFollowingPrefillStep }: TimeInAddressSituationProps): JSX.Element => {
  const appFormContext = useContext(ApplicationFormContext)
  const { featureSwitches } = appFormContext
  const eventsService = useAppFormEventsService()
  const { reviewPageV3 } = featureSwitches ?? {}
  const { hasNavigatedFromRequiredClick, setHasNavigatedFromRequiredClick } = useContext(PrefillReviewPageApplicationFormContext)
  const { dispatch } = useContext(AddressHistoryEntryContext)
  const { dispatch: appFormContextDispatch } = useContext(ApplicationFormContext)
  const { addressDuration, setAddressDuration } = useContext(PrefillReviewPageApplicationFormContext)
  const [years, setYears] = useState<string | undefined>(existingDuration?.years)
  const [months, setMonths] = useState<string | undefined>(existingDuration?.months)
  const [nextButtonCb, resetNextButtonCb] = useCallbackOnce()
  const [previousButtonCb] = useCallbackOnce()
  const [errors, setErrors] = useState<AddressEntryErrors>({})

  const lengthOfTimeValidator = AddressHistoryValidator.LengthOfTimeValidator().getValidator()
  const sumOfAddressDuration = calculateTotalMonths(addressDuration)
  const reviewPageV3AndClickedRequired = reviewPageV3 && hasNavigatedFromRequiredClick
  const over36MonthsAddressHistoryAndNotClickedRequired = sumOfAddressDuration >= 36 && !hasNavigatedFromRequiredClick

  const lengthOfTimeErrors = (): Errors => {
    const monthsError = errors?.months
    const yearsError = errors?.years
    const durationError = errors?.duration
    const errorObject: Errors = {
      message: yearsError || monthsError || durationError,
      fields: []
    }
    if (monthsError) {
      errorObject.fields.push('months')
    }
    if (yearsError) {
      errorObject.fields.push('years')
    }
    if (durationError) {
      errorObject.fields.push('duration')
    }

    return errorObject
  }

  const validate = (): boolean => {
    const schema = lengthOfTimeValidator

    const objectToValidate = {
      years,
      months
    }

    try {
      schema.validateSync(objectToValidate, {
        abortEarly: false
      })
    } catch (e: unknown) {
      const err = e as yup.ValidationError
      const prettyErrors: Record<string, string> = {}
      if (!err.inner) return false
      err.inner.forEach((validationError) => {
        if (!validationError.path) return
        const [errorMessage] = validationError.errors
        prettyErrors[validationError.path.toString()] = errorMessage
        fireReactGAEvent({
          category: 'Application Form',
          action: 'Validation Error',
          label: validationError.path.toString(),
          nonInteraction: true
        })
      })
      setErrors(prettyErrors)
      return false
    }

    return true
  }

  useEffect(() => {
    const label = `qst.addressHistory.${parseInt(entryId, 10) - 1}.timeAtAddress`
    fireReactGAEvent({
      category: 'Application Form',
      action: 'Question Visible',
      label,
      nonInteraction: true
    })
    eventsService?.sendEvent('AppFormQuestionVisible', { QuestionLabel: label })

    const updatedDuration = {
      years: Number(years) || 0,
      months: Number(months) || 0
    }

    if (updateFollowingPrefillStep) {
      setAddressDuration((prevDuration: { [entryId: string]: YearsMonthsDuration }) => {
        /*
          Filter out entries that are not up to the current entryId so that we can reach previous entry
          any additional time editing. (Used for navigation purposes only)
        */
        const filteredEntries: AddressDuration = Object.keys(prevDuration)
          .filter((key) => key <= entryId)
          .reduce((obj: AddressDuration, key: string) => {
            const newObj: AddressDuration = { ...obj }
            newObj[key] = prevDuration[key]
            return newObj
          }, {})

        const updatedEntries = { ...filteredEntries, [entryId]: updatedDuration }
        return updatedEntries
      })
    }
  }, [])

  const nextHandler = (): void => {
    if (!validate()) {
      resetNextButtonCb()
      return
    }

    dispatch({
      type: 'UPDATE_TIME_IN_SITUATION',
      value: buildLengthOfTimeEntry(years, months)
    })

    dispatch({
      type: 'NEXT'
    })

    setTimeout(() => {
      if (updateFollowingPrefillStep) {
        if (reviewPageV3AndClickedRequired || over36MonthsAddressHistoryAndNotClickedRequired) {
          updateFollowingPrefillStep(true, true, false)
        }
      }
    }, 50)

    if (hasNavigatedFromRequiredClick) {
      setHasNavigatedFromRequiredClick(false)
    }
  }

  const previousHandler = (): void => {
    appFormContextDispatch({
      type: 'NAVIGATED_FROM_QUESTION',
      questionName: `addressDuration-${parseInt(entryId, 10)}`,
      lastNavAction: 'PREVIOUS'
    })
    dispatch({
      type: 'UPDATE_TIME_IN_SITUATION',
      value: buildLengthOfTimeEntry(years, months)
    })

    dispatch({
      type: 'PREVIOUS'
    })
  }

  const valueChanged = (update: Record<string, unknown>): void => {
    const updatedDuration = {
      years,
      months
    }

    Object.keys(update).forEach((key) => {
      if (key === 'years') {
        updatedDuration.years = update.years as string
      }
      if (key === 'months') {
        updatedDuration.months = update.months as string
      }
    })

    setYears(updatedDuration.years)
    setMonths(updatedDuration.months)

    if (updateFollowingPrefillStep) {
      // TODO: Create a util function or custom hook to contain the logic here
      setAddressDuration((prevDuration) => {
        const updatedEntries = { ...prevDuration }
        const currentEntry = updatedEntries[entryId]

        /*
          We want to remove all entries after the current entryId if
          lengthOfTime is edited again. (Used for navigation purposes only)
        */
        if (currentEntry && (currentEntry.years !== Number(updatedDuration.years) || currentEntry.months !== Number(updatedDuration.months))) {
          const entryKeys = Object.keys(updatedEntries)
          const currentEntryIndex = entryKeys.indexOf(entryId)
          const entriesToRemove = entryKeys.slice(currentEntryIndex + 1)

          entriesToRemove.forEach((entryToRemove) => {
            delete updatedEntries[entryToRemove]
          })
        }

        updatedEntries[entryId] = {
          years: Number(updatedDuration.years) || 0,
          months: Number(updatedDuration.months) || 0
        }

        return updatedEntries
      })
    }
  }

  const handleEnterPress = (event: React.KeyboardEvent): void => {
    if (event.code === 'Enter' || event.key === 'Enter') {
      nextButtonCb(nextHandler)
    }
  }

  return (
    <>
      <GridItem
        gridColumn={['auto / span 12', '3 / span 8']}
        minHeight="174px"
      >
        <LengthOfTime
          valueChanged={valueChanged}
          onKeyPress={handleEnterPress}
          defaultValue={existingDuration}
          error={lengthOfTimeErrors()}
          trackingLabel={`qst.addressHistory.${parseInt(entryId, 10) - 1}.timeAtAddress`}
        >
          <AppFormQuestion
            mt={3}
            mb={3}
            questionKey={`${QuestionKey.ADDRESS_HISTORY}.lengthOfTime`}
            hintText={'We need your address history for the last 3 years. This is so our lenders can identify you and link to your credit history.'}
            textStyle={questionFontStyle}
            data-hj-suppress=""
          >
            {`How long ${Number(entryId) > 1 ? 'did you live' : 'have you lived'} ${entryTitle ? `at ${entryTitle}?` : 'here?'}`}
          </AppFormQuestion>
        </LengthOfTime>
      </GridItem>
      <NavigationButtons
        nextHandler={() => nextButtonCb(nextHandler)}
        previousHandler={() => previousButtonCb(previousHandler)}
        disableNext={!(months || years)}
        disablePrevious={disablePrevious ? 'invisible' : undefined}
      />
    </>
  )
}

export default withFade(TimeInAddressSituation)
